import React, { useEffect } from "react";

export interface PolarisConfirmModalProps {
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmLabel?: string;
    loadingLabel?: string;
    cancelLabel?: string;
    loading?: boolean;
    error?: string | null;
    onConfirm: () => void;
    onClose: () => void;
}

/**
 * Confirmation dialog styled to match the Polaris admin portal.
 * Replaces browser-native confirm() dialogs across the portal.
 */
export default function PolarisConfirmModal({
    open,
    title,
    message,
    confirmLabel = "Confirm",
    loadingLabel = "Working…",
    cancelLabel = "Cancel",
    loading = false,
    error,
    onConfirm,
    onClose,
}: PolarisConfirmModalProps) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (open && e.key === "Escape" && !loading) onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, loading, onClose]);

    if (!open) return null;

    const handleOverlayClick = () => {
        if (!loading) onClose();
    };

    return (
        <div
            className="pa-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pa-modal-title"
            onClick={handleOverlayClick}
        >
            <div className="pa-modal" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    className="pa-modal-close"
                    aria-label="Close"
                    onClick={onClose}
                    disabled={loading}
                >
                    ×
                </button>
                <h3 id="pa-modal-title" className="pa-modal-title">{title}</h3>
                <div className="pa-modal-message">{message}</div>
                {error && <p className="pa-modal-error" role="alert">{error}</p>}
                <div className="pa-modal-actions">
                    <button
                        type="button"
                        className="pa-btn pa-btn-secondary"
                        onClick={onClose}
                        disabled={loading}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        className="pa-btn pa-btn-primary"
                        onClick={onConfirm}
                        disabled={loading}
                        autoFocus
                    >
                        {loading ? loadingLabel : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
